import * as yup from "yup";

const idOf = (v: unknown): number | undefined =>
  v && typeof v === "object"
    ? (v as { id?: number }).id
    : undefined;

// Which pair the form is filling in. The fields of the other two are ignored, so switching the type
// never leaves a stale value blocking the submit.
type ThresholdMode = "station" | "city" | "country";

const modeOf = (opts: { context?: unknown }): ThresholdMode =>
  (opts?.context as { type?: ThresholdMode } | undefined)?.type ?? "station";

const CustomReservationThresholdSchema = yup.object().shape({
  fromStation: yup
    .object()
    .nullable()
    .test(
      "from-station-required",
      "Stacioni i nisjes eshte i detyrueshem.",
      function (value: unknown) {
        if (modeOf(this.options) !== "station") return true;
        return !!value;
      }
    ),

  toStation: yup
    .object()
    .nullable()
    .test(
      "to-station-required",
      "Stacioni i arritjes eshte i detyrueshem.",
      function (value: unknown) {
        if (modeOf(this.options) !== "station") return true;
        return !!value;
      }
    )
    .test(
      "not-same-station",
      "Stacioni i arritjes duhet te jete i ndryshem nga nisja.",
      function (value: unknown) {
        if (modeOf(this.options) !== "station") return true;
        const fromId = idOf(this.parent.fromStation);
        const toId = idOf(value);
        if (!fromId || !toId) return true;
        return fromId !== toId;
      }
    ),

  operator: yup.object().nullable(),

  fromCity: yup
    .object()
    .nullable()
    .test(
      "from-city-required",
      "Qyteti i nisjes eshte i detyrueshem.",
      function (value: unknown) {
        if (modeOf(this.options) !== "city") return true;
        return !!value;
      }
    ),

  toCity: yup
    .object()
    .nullable()
    .test(
      "to-city-required",
      "Qyteti i arritjes eshte i detyrueshem.",
      function (value: unknown) {
        if (modeOf(this.options) !== "city") return true;
        return !!value;
      }
    )
    .test(
      "not-same-city",
      "Qyteti i arritjes duhet te jete i ndryshem nga nisja.",
      function (value: unknown) {
        if (modeOf(this.options) !== "city") return true;
        const fromId = idOf(this.parent.fromCity);
        const toId = idOf(value);
        if (!fromId || !toId) return true;
        return fromId !== toId;
      }
    ),

  // Two countries may be the same on purpose: that is "every journey inside this country", so there is
  // no not-same test here.
  fromCountry: yup
    .object()
    .nullable()
    .test(
      "from-country-required",
      "Shteti i nisjes eshte i detyrueshem.",
      function (value: unknown) {
        if (modeOf(this.options) !== "country") return true;
        return !!value;
      }
    ),

  toCountry: yup
    .object()
    .nullable()
    .test(
      "to-country-required",
      "Shteti i arritjes eshte i detyrueshem.",
      function (value: unknown) {
        if (modeOf(this.options) !== "country") return true;
        return !!value;
      }
    ),

  threshold: yup
    .number()
    .typeError("Pragu duhet te jete numer.")
    .required("Pragu eshte i detyrueshem.")
    .min(1, "Pragu duhet te jete me i madh se 0.")
});

export default CustomReservationThresholdSchema;
